import { calculateAndSaveSaleData, removeDiscountFromCart } from "../../../../helpers/saleHelpers";
import { isOffline } from "../../../../facade/ServiceFacade";
import { SALE_SOURCE_TYPES } from "../../../../data/constants";
import {
  hasBillChangedForLoyalty,
  isBillChangeInvalidationSuppressed,
} from "../loyaltyRefundEffect";
import { isDotpeExternalDiscount } from "./shared";

export * from "./shared";

/**
 * WEB variant — the real body. See `./shared.ts` for what this effect is and why it is a folder.
 *
 * ⚠️ `releaseInFlight` is MODULE-LEVEL MUTABLE STATE, for the same reason `refundInFlight` is in
 * `../loyaltyRefundEffect`: `calculateAndSaveSaleData` dispatches `saveSaleData` SYNCHRONOUSLY,
 * which re-enters this very listener before the first pass has returned. Not in redux — a marker
 * that survived rehydration would wedge the effect for good.
 */
let releaseInFlight = false;

/**
 * Listener effect for `saveSaleData`.
 *
 * Fires when a DotPe CRM discount was on the bill before this change AND is still on it now, and
 * the bill changed in a way that invalidates it. `removeDiscountFromCart` sends the
 * `sale/customer/offer/unblock` call (DotPe is an external coupon provider as of DM-146) and
 * strips the entry; `calculateAndSaveSaleData` commits the result.
 *
 * The loyalty saga refetches the customer's balance and milestones once that unblock succeeds,
 * off the post body's `metaId` — no flag is passed from here. This removal is a DotPe one by
 * construction (`isDotpeExternalDiscount` selected the discount), so the refetch does fire.
 *
 * ⚠️ Sale TEARDOWN is not this effect's business — `resetSaleEffect` owns discard/reset, and
 * this one returns on a `mid` change for exactly that reason.
 *
 * 🔴 **The suppression check is the FIRST thing here, and it has to be.** "Change item" on an
 * already-redeemed milestone reward adds the newly-picked item to re-tie the reward — an add
 * that `hasBillChangedForLoyalty` cannot tell from any other, so this effect would release and
 * strip the very coupon that item is being added for. The caller arms
 * `skipNextBillChangeInvalidation()` for that one dispatch; see its header in
 * `../loyaltyRefundEffect` for why the arming is keyed to the action rather than consumed by
 * whichever effect looks first.
 */
export const dotpeOfferReleaseEffect = async (action: any, listenerApi: any) => {
  if (isBillChangeInvalidationSuppressed(action)) return;

  const prevSale = listenerApi.getOriginalState()?.saleDataStore?.saleData;
  const currentSale = listenerApi.getState()?.saleDataStore?.saleData;

  if (currentSale?.sourceInfo?.source === SALE_SOURCE_TYPES.API) return;
  if (isOffline()) return; // the unblock is server-backed
  if (releaseInFlight) return;
  if (prevSale?.mid !== currentSale?.mid) return;

  const prevDiscount = prevSale?.billDiscountList?.find(isDotpeExternalDiscount);
  const currDiscount = currentSale?.billDiscountList?.find(isDotpeExternalDiscount);
  if (!prevDiscount || !currDiscount) return;

  if (!hasBillChangedForLoyalty(prevSale, currentSale)) return;

  releaseInFlight = true;
  try {
    const updatedSale = removeDiscountFromCart(currentSale, currDiscount);
    calculateAndSaveSaleData(updatedSale);
  } finally {
    releaseInFlight = false;
  }
};
